import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useApolloClient } from '@apollo/react-hooks';
import { differenceInMinutes } from 'date-fns';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { DiscountValueTypes } from 'enums/menu';
import {
  GetOrderDocument,
  RbiOrderStatus,
  useCommitOrderMutation,
  useGetLoyaltyCardTransactionsLazyQuery,
  useGetUserOrdersLazyQuery,
  usePriceOrderMutation,
  useUpdateOrderMutation
} from 'generated/rbi-graphql';
import { useKioskAppDataQuery } from 'generated/sanity-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useErrorModal from 'hooks/use-error-modal';
import useRoutes from 'hooks/use-routes';
import { getOrderFailureMessageKey } from 'pages/cart/payment/get-error-dialog-options';
import { useAuthContext } from 'state/auth';
import { useCustomizableContent } from 'state/customizable-content';
import { useErrorContext } from 'state/errors';
import { useFraudPreventionContext } from 'state/fraud-prevention';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useLoggerContext } from 'state/logger';
import { useThLoyaltyContext } from 'state/loyalty';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useOffersContext } from 'state/offers';
import { useCartTip } from 'state/order/hooks/use-cart-tip';
import { usePaymentContext } from 'state/payment';
import { useServiceModeContext } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { cartEntryIsAvailable } from 'utils/availability';
import {
  CartEntryType,
  buildCommitDeliveryInput,
  buildCommitOrderInput,
  buildPriceDeliveryInput,
  buildStoreAddress,
  remappedCartForBackEnd
} from 'utils/cart';
import { computeCartTotal, computeOtherDiscountAmount } from 'utils/cart/helper';
import { redeemReward } from 'utils/cart/redeem-reward';
import * as DatadogLogger from 'utils/datadog';
import { brand, isKiosk, isNative, platform } from 'utils/environment';
import {
  GraphQLErrorCodes,
  getAppleOrGooglePayError,
  getErrorDialogForGraphQLCode,
  parseGraphQLErrorCodes
} from 'utils/errors';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import kioskUsernameValidator from 'utils/form/kiosk-user-name';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import noop from 'utils/noop';
import { routes } from 'utils/routing';
import { isCatering as isCateringOrder } from 'utils/service-mode';
import { Measures, PerformanceMarks, getMeasureAndClearMarks, setMark } from 'utils/timing';

import { TipAmounts } from './constants';
import { checkLimitReachedEvent, checkMinimumNotReachedEvent } from './custom-events';
import useAlertOrderCateringMinimum from './hooks/use-alert-order-catering-min';
import useAlertOrderDeliveryMinimum from './hooks/use-alert-order-delivery-min';
import useAlertOrderLimit from './hooks/use-alert-order-limit';
import { useHandleReorder } from './hooks/use-handle-reorder';
import useRewardDiscount from './hooks/use-reward-discount';
import { useUnavailableCartEntries } from './hooks/use-unavailable-cart-entries';
import { orderPollFailure, orderPollSuccessful } from './order-state-utils';
import refetchTransactions from './refetch-transactions';
import { DeliveryStatus, OrderStatus } from './types';
import { determineLimit } from './utils';

export { OrderStatus, ServiceMode, TipAmounts };
export const OrderContext = React.createContext();
export const useOrderContext = () => useContext(OrderContext);

const preloadedOrder = () => {
  const order = LocalStorage.getItem(StorageKeys.ORDER);
  if (order) {
    const { curbsidePickupOrderTimePlaced } = order;
    // We time out saved curbside order after 1 hour
    // if user has not submitted the order to prevent
    // outdated order
    if (
      !curbsidePickupOrderTimePlaced ||
      differenceInMinutes(new Date(), new Date(curbsidePickupOrderTimePlaced)) > 60
    ) {
      return {
        ...order,
        curbsidePickupOrderTimePlaced: '',
        curbsidePickupOrderId: ''
      };
    }
    return order;
  }
  return {};
};

/**
 * NOTE update this on cart shape changes.
 * ! Only update on breaking changes to cart shape.
 */
export const CART_VERSION = 1;
/*
  type CartEntry = {
    cartId: string | number,
    _id: string,
    name: string,
    url: string,
    image: string,
    quantity: number,
    price: number,
    children?: Array<{ _id: string, name: string }>
  };
*/

const useKioskAppData = () => {
  const { appDataId } = useCustomizableContent();
  const { data } = useKioskAppDataQuery({ variables: { appDataId }, skip: !appDataId });
  const { kioskAppData } = data?.AppData || {};
  return kioskAppData || {};
};

export function OrderProvider(props) {
  const apolloClient = useApolloClient();
  // should all this (42-302) maybe be broken out into
  // hooks based on function, i.e. `useCart`, `useStoreSelect`?
  const deliveryBannerPolling = useFlag(LaunchDarklyFlag.DELIVERY_BANNER_POLLING);
  const tipPercentThresholdCents = useFlag(LaunchDarklyFlag.TIP_PERCENT_THRESHOLD_CENTS) || 0;
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const preloaded = useMemo(() => preloadedOrder(), []);
  const auth = useAuthContext();
  const billingCountry = auth?.user?.details?.isoCountryCode || ISOs.USA;
  const { currencyCode } = getCountryAndCurrencyCodes(billingCountry);
  const { decorateLogger, logger } = useLoggerContext();
  const mParticle = useMParticleContext();
  const { pricingFunction, priceForItemOptionModifier } = useMenuContext();
  const { userNameBlacklist, blacklistCharacterReplaceRules } = useKioskAppData();
  const { getPaymentMethods, defaultPaymentMethodId } = usePaymentContext();
  const {
    tipAmount,
    setTipAmount,
    tipSelection,
    setTipSelection,
    updateTipAmount,
    shouldShowTipPercentage
  } = useCartTip();

  const {
    prices,
    resetStore,
    selectStore: selectNewStore,
    store,
    resetLastTimeStoreUpdated
  } = useStoreContext();
  const { serviceMode, setServiceMode } = useServiceModeContext();
  const { location, navigate } = useLocationContext();
  const offers = useOffersContext();
  const { setCurrentOrderId } = useErrorContext();
  const { vendor: posVendor } = usePosVendor();
  const [ErrorDialog, openErrorDialog, dismissDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Order Error'
  });
  const {
    barcodeScanner: { clearData: clearScannedLoyaltyCard, deviceData: scannedLoyaltyCard }
  } = useThLoyaltyContext();
  const { refetchActiveCard, activeCard } = useLoyaltyActiveCard();
  const { kountSessionId, resetKountSessionId } = useFraudPreventionContext();

  const { cardId, barcode } = activeCard || {};
  const [refetchActiveCardTransactions] = useGetLoyaltyCardTransactionsLazyQuery({
    fetchPolicy: 'network-only',
    variables: {
      cardId
    }
  });
  const [refetchGetUserOrders] = useGetUserOrdersLazyQuery({
    fetchPolicy: 'network-only',
    variables: {
      limit: 1,
      orderStatuses: [RbiOrderStatus.INSERT_SUCCESSFUL, RbiOrderStatus.UPDATE_SUCCESSFUL]
    }
  });

  const [executeUpdateOrderMutation] = useUpdateOrderMutation();
  const [executeCommitOrderMutation] = useCommitOrderMutation();
  const [executePriceOrderMutation] = usePriceOrderMutation();

  const { getLocalizedRouteForPath } = useRoutes();

  const [pendingReorder, setPendingReorder] = useState(null);
  const [reordering, setReordering] = useState(false);

  const [cartEntries, setCartEntries] = useState(preloaded.cartEntries || []);
  const [curbsidePickupOrderId, setCurbsidePickupOrderId] = useState(
    preloaded.curbsidePickupOrderId || ''
  );
  const [curbsidePickupOrderTimePlaced, setCurbsidePickupOrderTimePlaced] = useState(
    preloaded.curbsidePickupOrderTimePlaced || ''
  );
  const [serverOrder, setServerOrder] = useState({});
  const [kioskUserName, setKioskUserName] = useState({
    isValid: false,
    name: ''
  });
  const [cateringPickupDateTime, setOrderCateringPickupDateTime] = useState(
    preloaded.cateringPickupDateTime || ''
  );
  const [deliveryAddress, setDeliveryAddress] = useState(preloaded.deliveryAddress || {});
  const [deliveryInstructions, setDeliveryInstructions] = useState(
    preloaded.deliveryInstructions || ''
  );
  const [deliveryPhoneNumber, setDeliveryPhoneNumber] = useState(
    preloaded.deliveryPhoneNumber || ''
  );
  const [fireOrderIn, setFireOrderIn] = useState(0);
  const [cartPriceLimitExceeded, setCartPriceLimitExceeded] = useState(false);
  const [cartPriceTooLow, setCartPriceTooLow] = useState(false);
  const [cartCateringPriceTooLow, setCartCateringPriceTooLow] = useState(false);

  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const refCommitOrderTimeout = useRef(null);
  const refPriceOrderTimeout = useRef(null);

  const { unavailableCartEntries, setUnavailableCartEntries } = useUnavailableCartEntries({
    serverOrder,
    cartEntries
  });
  const [pendingRecentItem, setPendingRecentItem] = useState();

  // th specific: reward information
  const { cartHasRewardEligibleItem } = useRewardDiscount({
    serverOrder,
    cartEntries
  });

  const updateShouldSaveDeliveryAddress = useCallback(shouldSaveDeliveryAddress => {
    setDeliveryAddress(previousAddress => ({
      ...previousAddress,
      shouldSave: shouldSaveDeliveryAddress
    }));
  }, []);

  const orderLimitMessage = (maxLimit, maxCateringLimit, isOrderCatering) => {
    const limit = determineLimit({ maxLimit, maxCateringLimit, isCatering: isOrderCatering });
    return formatMessage(
      { id: 'orderLimitMessage' },
      {
        maxLimit: formatCurrencyForLocale(limit)
      }
    );
  };

  const shouldOfferBeRemoved = useCallback(
    item => {
      const cartEntriesWithRemovedItem = cartEntries.filter(entry => entry._id !== item._id);
      const offerDetails = {
        selectedOffer: offers.selectedOffer,
        selectedOfferCartEntry: offers.selectedOfferCartEntry,
        selectedOfferPrice: offers.selectedOfferPrice
      };
      const total = computeCartTotal(offerDetails, cartEntriesWithRemovedItem);

      const offerPrice = offers?.selectedOffer?.option?.discountValue || 0;
      return total < offerPrice * 100;
    },
    [cartEntries, offers]
  );

  const getOfferText = item =>
    shouldOfferBeRemoved(item) ? formatMessage({ id: 'removeItemFromCartOfferText' }) : '';

  const removeItemMessage = item => {
    return item
      ? formatMessage(
          { id: 'removeItemFromCart' },
          {
            item: item.name,
            offerText: getOfferText(item)
          }
        )
      : '';
  };

  const setKioskUserNameFiltered = useCallback(
    name => {
      const customBadWords = userNameBlacklist || [];
      const replacementRules = blacklistCharacterReplaceRules || [];
      const { validateKioskUserName } = kioskUsernameValidator(customBadWords, replacementRules);

      const isValid = validateKioskUserName(name);
      setKioskUserName({
        isValid,
        name
      });
    },
    [blacklistCharacterReplaceRules, userNameBlacklist]
  );

  const emptyCart = useCallback(() => {
    setCartEntries([]);
    offers.clearSelectedOffer({ doLog: false });
    offers.setRedemptionMethod(null);
    setTipAmount(0);
    setTipSelection({
      percentAmount: TipAmounts.PERCENT_DEFAULT,
      dollarAmount: TipAmounts.DOLLAR_DEFAULT,
      otherAmount: 0,
      isOtherSelected: false
    });
  }, [offers, setTipAmount, setTipSelection]);

  useEffectOnUpdates(() => {
    if (!auth.isAuthenticated()) {
      emptyCart();
    }
  }, [auth.user]); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyCartVersion = useCallback(
    (version, { message, onFailure = noop, onSuccess = noop }) => {
      if (version !== CART_VERSION) {
        if (message) {
          openErrorDialog({ message, modalAppearanceEventMessage: 'Error: Cart Version Mismatch' });
        }
        onFailure();
        return;
      }
      onSuccess();
    },
    [openErrorDialog]
  );

  useEffect(() => {
    const updateStoredCartVersion = () =>
      LocalStorage.setItem(StorageKeys.ORDER, {
        ...preloaded,
        cartVersion: CART_VERSION
      });

    const cartVersionArgs = {
      message: cartEntries.length > 0 ? formatMessage({ id: 'outdatedCartVersionMessage' }) : '',
      onFailure: () => {
        if (cartEntries.length > 0) {
          emptyCart();
        }
        updateStoredCartVersion();
      }
    };

    verifyCartVersion(preloaded.cartVersion || 0, cartVersionArgs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCurrentOrderId(serverOrder.rbiOrderId);
  }, [serverOrder, setCurrentOrderId]);

  // for confirming before changing modes
  const onConfirmNewMode = useCallback(
    ({ newMode, resolve }) => {
      mParticle.selectServiceMode(newMode);
      setServiceMode(newMode);
      emptyCart();
      resetLastTimeStoreUpdated();
      return resolve(true);
    },
    [emptyCart, mParticle, setServiceMode, resetLastTimeStoreUpdated]
  );

  const [ServiceModeDialog, openSvcModeDialog] = useDialogModal({
    onConfirm: onConfirmNewMode,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Selecting new store may change prices'
  });

  const selectServiceMode = useCallback(
    newMode => {
      const isChangingToOrFromDelivery =
        newMode === ServiceMode.DELIVERY || serviceMode === ServiceMode.DELIVERY;

      if (isChangingToOrFromDelivery && cartEntries.length) {
        return new Promise(resolve => {
          if (serviceMode !== newMode) {
            openSvcModeDialog({ newMode, resolve });
          } else {
            resolve(true);
          }
        });
      }

      mParticle.selectServiceMode(newMode);
      setTipAmount(0);
      setTipSelection({
        percentAmount: TipAmounts.PERCENT_DEFAULT,
        dollarAmount: TipAmounts.DOLLAR_DEFAULT,
        otherAmount: 0,
        isOtherSelected: false
      });
      setServiceMode(newMode);
      resetLastTimeStoreUpdated();
      return Promise.resolve(true);
    },
    [
      serviceMode,
      cartEntries.length,
      mParticle,
      setTipAmount,
      setTipSelection,
      setServiceMode,
      resetLastTimeStoreUpdated,
      openSvcModeDialog
    ]
  );

  const { onConfirmRedemption, selectedOffer, clearSelectedOffer } = offers;

  const logCartEntryRemovedFromCart = useCallback(
    cartEntry => {
      mParticle.removeFromCart(cartEntry);
      if (cartEntry.isUpsell) {
        mParticle.logUpsellRemovedEvent(cartEntry);
      }
    },
    [mParticle]
  );

  const removeOfferFromCart = useCallback(() => {
    return selectedOffer && clearSelectedOffer();
  }, [clearSelectedOffer, selectedOffer]);

  // for confirming item removal
  const removeFromCart = useCallback(
    item => {
      setCartEntries(prevCartEntries =>
        prevCartEntries.filter(entry => item.cartId !== entry.cartId)
      );
      logCartEntryRemovedFromCart(item);
      if (shouldOfferBeRemoved(item)) {
        removeOfferFromCart();
      }
    },
    [logCartEntryRemovedFromCart, removeOfferFromCart, shouldOfferBeRemoved]
  );

  const removeAllFromCart = useCallback(
    cartEntriesToRemove => {
      const cartEntryIdsToRemove = cartEntriesToRemove.map(entry => entry.cartId);
      setCartEntries(prevCartEntries =>
        prevCartEntries.filter(entry => !cartEntryIdsToRemove.includes(entry.cartId))
      );
      cartEntriesToRemove.map(logCartEntryRemovedFromCart);
    },
    [logCartEntryRemovedFromCart]
  );

  const [RemoveItemDialog, openRemoveItemDialog, itemToRm] = useDialogModal({
    onConfirm: removeFromCart,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Remove item from cart'
  });

  const confirmRemoveFromCart = useCallback(
    cartId => {
      const entry = cartEntries.find(item => cartId === item.cartId);
      openRemoveItemDialog(entry);
    },
    [cartEntries, openRemoveItemDialog]
  );

  const calculateCartTotal = useCallback(() => {
    return computeCartTotal(offers, cartEntries);
  }, [cartEntries, offers]);

  const logCartStoreAndTimeout = (resetStoreTimeout, timeSinceLastVisit) => {
    const storeDetails = {
      cartEntriesTotal: cartEntries.length,
      storeId: store._id,
      itemNames: cartEntries.map(entry => entry.name)
    };

    const { cartEntriesTotal, storeId, itemNames } = storeDetails;

    mParticle.logEvent(CustomEventNames.SESSION_RESET_FROM_INACTIVITY, EventTypes.Other, {
      storeDetails: `cartEntriesTotal: ${cartEntriesTotal}, storeId: ${storeId}, itemNames: ${JSON.stringify(
        itemNames
      )} `,
      resetStoreTimeoutSeconds: resetStoreTimeout,
      hoursSinceLastVisit: parseInt((timeSinceLastVisit / (1000 * 60 * 60)).toFixed(1))
    });
  };

  const clearCartStoreServiceModeTimeout = () => {
    selectServiceMode(null);
    setUnavailableCartEntries([]);
    emptyCart();
    resetStore();
    LocalStorage.removeItem(StorageKeys.LAST_TIME_STORE_UPDATED);
  };

  const checkoutPriceLimit = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_LIMIT);
  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  const checkoutCateringPriceLimit = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_CATERING_LIMIT);
  const checkoutCateringPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_CATERING_MINIMUM);

  const [OrderLimitDialog, openOrderLimitDialog] = useDialogModal({
    modalAppearanceEventMessage: 'Order limit reached'
  });

  const alertOrderLimit = useCallback(() => {
    checkLimitReachedEvent(calculateCartTotal(), mParticle);
    openOrderLimitDialog();
  }, [mParticle, calculateCartTotal, openOrderLimitDialog]);
  const alertOrderDeliveryMinimum = useCallback(() => {
    if (
      cartEntries.length > 0 &&
      isDelivery &&
      calculateCartTotal() < checkoutDeliveryPriceMinimum
    ) {
      checkMinimumNotReachedEvent(calculateCartTotal(), mParticle);
    }
  }, [mParticle, calculateCartTotal, cartEntries.length, checkoutDeliveryPriceMinimum, isDelivery]);

  const alertOrderCateringMinimum = useCallback(() => {
    if (
      cartEntries.length > 0 &&
      isCateringOrder(serviceMode) &&
      calculateCartTotal() < checkoutCateringPriceMinimum
    ) {
      checkMinimumNotReachedEvent(calculateCartTotal(), mParticle);
    }
  }, [
    mParticle,
    calculateCartTotal,
    cartEntries.length,
    checkoutCateringPriceMinimum,
    serviceMode
  ]);
  const updateQuantity = useCallback(
    (cartId, quantity) => {
      if (quantity < 1) {
        return removeFromCart(cartId);
      }

      setCartEntries(prevCartEntries =>
        prevCartEntries.map(entry => {
          if (entry.cartId === cartId) {
            entry.quantity = quantity;
            entry.price = pricingFunction(entry, entry.quantity);
          }

          return entry;
        })
      );
    },
    [pricingFunction, removeFromCart]
  );

  const updateCartEntry = cartEntry => {
    // remove updated item from mParticle cart
    mParticle.removeFromCart(cartEntry);
    setCartEntries(prevCartEntries => {
      const index = prevCartEntries.findIndex(
        entry => String(entry.cartId) === String(cartEntry.cartId)
      );
      // wholesale replace
      prevCartEntries = [
        ...prevCartEntries.slice(0, index),
        ...cartEntry,
        ...prevCartEntries.slice(index + 1)
      ];
      return prevCartEntries;
    });
    // add updated item to mParticle cart
    mParticle.addToCart(cartEntry, serviceMode);
  };

  const addToCart = useCallback(
    cartEntry => {
      setCartEntries(prevCartEntries => prevCartEntries.concat([cartEntry]));
      mParticle.addToCart(cartEntry, serviceMode);
    },
    [setCartEntries, mParticle, serviceMode]
  );

  const resetKioskState = useCallback(() => {
    setCartEntries([]);
    setServiceMode(null);
    setKioskUserNameFiltered('');
    clearScannedLoyaltyCard();
  }, [clearScannedLoyaltyCard, setKioskUserNameFiltered, setServiceMode]);

  const logOrderLatencyDuration = useCallback(
    (actionType, remoteOrder) => {
      if (!['commit', 'price'].includes(actionType)) {
        return;
      }

      const { endMark, startMark } =
        actionType === 'price'
          ? { endMark: PerformanceMarks.PRICE_END, startMark: PerformanceMarks.PRICE_START }
          : { endMark: PerformanceMarks.COMMIT_END, startMark: PerformanceMarks.COMMIT_END };

      const measure = actionType === 'price' ? Measures.PRICE : Measures.COMMIT;

      const duration = getMeasureAndClearMarks(measure, startMark, endMark);

      if (duration !== undefined) {
        mParticle.logOrderLatencyEvent(remoteOrder, actionType, duration);
      }
    },
    [mParticle]
  );

  const queryOrder = useCallback(
    async rbiOrderId => {
      try {
        const { data, errors } = await apolloClient.query({
          fetchPolicy: 'network-only',
          query: GetOrderDocument,
          variables: {
            rbiOrderId
          }
        });

        if (errors) {
          throw errors;
        }

        return data?.order;
      } catch (error) {
        logger.error({ error, message: 'Error querying order' });
        throw error;
      }
    },
    [apolloClient, logger]
  );

  // commit was being fired twice, which was firing onCommitSuccess twice
  // the lastPurchaseOrderRId checks if the same order is being committed twice
  // where we set .current = rbiOrderID is on the first run, so the second run will return early
  const lastPurchaseOrderId = useRef();
  const onCommitSuccess = useCallback(
    async remoteOrder => {
      if (lastPurchaseOrderId.current === remoteOrder.rbiOrderId) {
        return;
      }
      if (remoteOrder?.cart?.serviceMode === ServiceMode.CURBSIDE) {
        setCurbsidePickupOrderId('');
        setCurbsidePickupOrderTimePlaced('');
      }
      lastPurchaseOrderId.current = remoteOrder.rbiOrderId;
      emptyCart();
      resetKountSessionId();
      const cartEntriesData = offers.selectedOfferCartEntry
        ? [...cartEntries, offers.selectedOfferCartEntry]
        : cartEntries;
      const deliveryFeeCents = remoteOrder.delivery?.feeCents || 0;
      const deliveryFeeDiscountCents = remoteOrder.delivery?.feeDiscountCents || 0;
      const deliveryServiceFeeCents = remoteOrder.delivery?.serviceFeeCents || 0;
      const deliverySmallCartFeeCents = remoteOrder.delivery?.smallCartFeeCents || 0;
      const ccToken = remoteOrder.cart?.payment?.panToken || null;
      mParticle.logPurchase(cartEntriesData, store, serviceMode, remoteOrder, {
        currencyCode,
        ccToken,
        deliveryFeeCents:
          deliveryFeeCents -
          deliveryFeeDiscountCents -
          deliveryServiceFeeCents -
          deliverySmallCartFeeCents,
        deliveryFeeDiscountCents,
        deliveryServiceFeeCents,
        deliverySmallCartFeeCents,
        fireOrderInMinutes: Math.round(fireOrderIn / 60)
      });
      setServerOrder(remoteOrder);

      try {
        await Promise.all([
          selectedOffer ? onConfirmRedemption(selectedOffer) : Promise.resolve(),
          // refresh the user's payment methods stored
          // in state, including gift cards
          getPaymentMethods(),

          // refresh user's active loyalty card
          refetchActiveCard(),
          // refresh user's active loyalty card transactions
          refetchTransactions ? refetchActiveCardTransactions() : Promise.resolve(),
          // refresh last order for delivery banner
          deliveryBannerPolling ? refetchGetUserOrders() : Promise.resolve()
        ]);
      } catch (error) {
        logger.error({ error, message: 'Error after commit success' });
      }
    },
    [
      emptyCart,
      resetKountSessionId,
      offers.selectedOfferCartEntry,
      cartEntries,
      mParticle,
      store,
      serviceMode,
      currencyCode,
      fireOrderIn,
      selectedOffer,
      onConfirmRedemption,
      getPaymentMethods,
      refetchActiveCard,
      refetchActiveCardTransactions,
      deliveryBannerPolling,
      refetchGetUserOrders,
      logger
    ]
  );

  const commit = useCallback(
    async ({
      billingAddress,
      ccBin,
      ccExpiry,
      ccLast4,
      creditType,
      fdAccessToken,
      fdAccountId,
      fdNonce,
      fullName,
      order,
      rbiOrderId,
      storeEmail
    }) => {
      setMark(PerformanceMarks.COMMIT_START);

      const pollForCommit = async orderId => {
        if (
          ![routes.cart, routes.orderConfirmation].find(route =>
            location.pathname.startsWith(route)
          )
        ) {
          clearTimeout(refCommitOrderTimeout.current);
          return Promise.resolve(null);
        }

        const remoteOrder = await queryOrder(orderId);

        const success = orderPollSuccessful({
          deliverySuccessStatus: DeliveryStatus.ORDER_CREATED,
          isDelivery,
          order: remoteOrder,
          orderSuccessStatus: [OrderStatus.INSERT_SUCCESSFUL, OrderStatus.CATERING_SUCCESSFUL]
        });
        const failure = orderPollFailure({
          deliveryFailureStatus: [
            DeliveryStatus.QUOTE_ERROR,
            DeliveryStatus.ORDER_ERROR,
            DeliveryStatus.ORDER_TIMEOUT
          ],
          isDelivery,
          order: remoteOrder,
          orderFailureStatus: [
            OrderStatus.INSERT_ERROR,
            OrderStatus.PAYMENT_ERROR,
            OrderStatus.CATERING_ERROR
          ]
        });

        setServerOrder(remoteOrder || {});

        if (success || failure) {
          setMark(PerformanceMarks.COMMIT_END);

          logOrderLatencyDuration('commit', remoteOrder);
        }

        if (success) {
          onCommitSuccess(remoteOrder);

          return remoteOrder.status;
        } else if (failure) {
          // throw error based on status
          throw new Error(remoteOrder?.status || OrderStatus.PRICE_ERROR);
        } else {
          return new Promise(resolve => {
            refCommitOrderTimeout.current = setTimeout(
              () => resolve(pollForCommit(remoteOrder.rbiOrderId)),
              1000
            );
          });
        }
      };

      const input = buildCommitOrderInput({
        billingAddress,
        ccBin,
        ccExpiry,
        ccLast4,
        creditType,
        fdAccessToken,
        fdAccountId,
        fdNonce,
        fireOrderIn,
        fullName,
        order,
        rbiOrderId,
        kountSessionId,
        storeEmail
      });

      const deliveryInput = buildCommitDeliveryInput(order);

      try {
        const { data, errors } = await executeCommitOrderMutation({
          variables: {
            delivery: deliveryInput,
            input
          }
        });

        if (errors) {
          throw errors;
        }

        const { rbiOrderId: orderId, status } = data?.commitOrder || {};

        resetKountSessionId();
        setServerOrder(currentServerOrder => ({ ...currentServerOrder, status }));
        return pollForCommit(orderId);
      } catch (error) {
        resetKountSessionId();
        const [errorCode] = parseGraphQLErrorCodes(error);
        const errorDialog =
          errorCode && getErrorDialogForGraphQLCode(errorCode, formatMessage, error);

        // Provide an error dialog specific to the GQL error code if available
        if (errorDialog) {
          const applePayOrGooglePayError = getAppleOrGooglePayError(fdAccountId || '');

          const isPaymentError =
            errorCode.errorCode === GraphQLErrorCodes.PAYMENTS_DECLINED_ERROR ||
            errorCode.errorCode === GraphQLErrorCodes.PAYMENTS_ERROR;

          if (isPaymentError && errorDialog && applePayOrGooglePayError) {
            errorDialog.message = formatMessage({ id: applePayOrGooglePayError });
          }

          openErrorDialog({
            ...errorDialog,
            error,
            modalAppearanceEventMessage: 'Error: Order Commit Error'
          });
        } else {
          const errorMsgId = getOrderFailureMessageKey(defaultPaymentMethodId);
          openErrorDialog({
            title: formatMessage({ id: 'uhoh' }),
            error,
            message: formatMessage({ id: errorMsgId }),
            modalAppearanceEventMessage: 'Error: Order Commit Error',
            body: formatMessage(
              { id: errorMsgId },
              {
                link: (
                  <Link to={getLocalizedRouteForPath('contact-us')} onClick={dismissDialog}>
                    {formatMessage({ id: 'here' })}.
                  </Link>
                )
              }
            )
          });
        }
        // This throw is vitally important.
        // Downstream catches exist to handle loading states and
        // if we do not throw, then the downstream catch is not called.
        throw error;
      }
    },
    [
      executeCommitOrderMutation,
      queryOrder,
      fireOrderIn,
      kountSessionId,
      location.pathname,
      isDelivery,
      logOrderLatencyDuration,
      onCommitSuccess,
      resetKountSessionId,
      formatMessage,
      openErrorDialog,
      defaultPaymentMethodId,
      getLocalizedRouteForPath,
      dismissDialog
    ]
  );

  const price = useCallback(
    async paymentMethod => {
      setMark(PerformanceMarks.PRICE_START);

      const pollForPrice = async orderId => {
        if (!location.pathname.startsWith(routes.cart)) {
          clearTimeout(refPriceOrderTimeout.current);

          return Promise.resolve(null);
        }

        const remoteOrder = await queryOrder(orderId);

        const success = orderPollSuccessful({
          deliverySuccessStatus: DeliveryStatus.QUOTE_SUCCESSFUL,
          isDelivery,
          order: remoteOrder,
          orderSuccessStatus: OrderStatus.PRICE_SUCCESSFUL
        });
        const failure = orderPollFailure({
          deliveryFailureStatus: [DeliveryStatus.QUOTE_ERROR, DeliveryStatus.QUOTE_UNAVAILABLE],
          isDelivery,
          order: remoteOrder,
          orderFailureStatus: OrderStatus.PRICE_ERROR
        });

        if (success || failure) {
          setMark(PerformanceMarks.PRICE_END);

          logOrderLatencyDuration('price', remoteOrder);
        }

        if (success) {
          await offers.validateOfferRedeemable({
            selectedOffer: offers.selectedOffer,
            rbiOrderId: remoteOrder.rbiOrderId
          });

          setServerOrder(remoteOrder);

          if (isDelivery) {
            const otherDiscountAmount = computeOtherDiscountAmount(remoteOrder.cart.discounts);
            updateTipAmount({ subTotalCents: remoteOrder.cart.subTotalCents, otherDiscountAmount });
          }

          return remoteOrder.status;
        } else if (failure) {
          setServerOrder(remoteOrder || {});

          throw new Error(OrderStatus.PRICE_ERROR);
        } else {
          return new Promise(resolve => {
            refPriceOrderTimeout.current = setTimeout(
              () => resolve(pollForPrice(remoteOrder.rbiOrderId)),
              1000
            );
          });
        }
      };

      // create offer cart item with updated plus
      const cartEntriesWithSelectedOffer = offers.selectedOfferCartEntry
        ? [
            {
              ...offers.selectedOfferCartEntry,
              price: offers.selectedOfferPrice,
              offerVendorConfigs: offers.selectedOffer.vendorConfigs,
              type: `Offer${offers.selectedOfferCartEntry.type}`
            }
          ].concat(cartEntries)
        : cartEntries;

      const priceInCents = calculateCartTotal(cartEntriesWithSelectedOffer);
      const storeAddress = buildStoreAddress(store);
      const remappedCartEntries = remappedCartForBackEnd(cartEntriesWithSelectedOffer);

      const orderInput = {
        calculateCartTotal,
        cartEntries: cartEntriesWithSelectedOffer,
        cartVersion: CART_VERSION,
        customerName: isKiosk ? kioskUserName.name : null,
        deliveryAddress,
        deliveryPhoneNumber,
        loyaltyBarcode: scannedLoyaltyCard.data || barcode,
        paymentMethod,
        redeemReward,
        serviceMode,
        store
      };

      const deliveryInput = buildPriceDeliveryInput(orderInput, auth.user);

      try {
        const { data, errors } = await executePriceOrderMutation({
          variables: {
            delivery: deliveryInput,
            input: {
              ...orderInput,
              brand: brand().toUpperCase(),
              storeAddress,
              cartEntries: remappedCartEntries,
              platform: platform(),
              posVendor: store.pos.vendor,
              requestedAmountCents: Math.round(priceInCents),
              storeId: store.number,
              storePosId: store.posRestaurantId
            }
          }
        });

        if (errors) {
          throw errors;
        }

        const remoteOrder = data.priceOrder;

        if (!remoteOrder) {
          throw new Error(OrderStatus.PRICE_ERROR);
        }
        return pollForPrice(remoteOrder.rbiOrderId);
      } catch (error) {
        logger.error({ error, message: 'Error pricing order' });
        throw error;
      }
    },
    [
      offers,
      cartEntries,
      calculateCartTotal,
      store,
      kioskUserName.name,
      deliveryAddress,
      deliveryPhoneNumber,
      scannedLoyaltyCard.data,
      barcode,
      serviceMode,
      auth.user,
      location.pathname,
      queryOrder,
      isDelivery,
      logOrderLatencyDuration,
      updateTipAmount,
      executePriceOrderMutation,
      logger
    ]
  );

  const getAndRefreshServerOrder = useCallback(
    async id => {
      try {
        const order = await queryOrder(id);
        setServerOrder(order || {});
        return order;
      } catch (error) {
        logger.error({ error, message: 'Error refreshing order' });
      }
    },
    [queryOrder, logger]
  );

  const fireOrderInXSeconds = useCallback(
    async ({ rbiOrderId, timeInSeconds }) => {
      try {
        const { data, errors } = await executeUpdateOrderMutation({
          variables: {
            input: {
              fireOrderIn: timeInSeconds,
              rbiOrderId
            }
          }
        });

        if (errors) {
          throw errors;
        }

        return getAndRefreshServerOrder(data?.updateOrder?.rbiOrderId);
      } catch (error) {
        logger.error({ error, message: 'Error updating order.' });
        throw error;
      }
    },
    [executeUpdateOrderMutation, getAndRefreshServerOrder, logger]
  );

  const clearServerOrder = () => {
    setServerOrder({});
  };

  // make sure we persist everything!
  useEffect(() => {
    LocalStorage.setItem(StorageKeys.ORDER, {
      cartEntries,
      cartVersion: CART_VERSION,
      cateringPickupDateTime,
      deliveryAddress,
      deliveryInstructions,
      deliveryPhoneNumber,
      curbsidePickupOrderId,
      curbsidePickupOrderTimePlaced
    });
  }, [
    cartEntries,
    cateringPickupDateTime,
    serviceMode,
    deliveryInstructions,
    deliveryPhoneNumber,
    deliveryAddress,
    curbsidePickupOrderId,
    curbsidePickupOrderTimePlaced
  ]);

  // Configure the logger to hold some information
  useEffect(() => {
    const extras = {
      cartEntries: JSON.stringify(cartEntries, null, 4),
      serviceMode
    };

    if (serverOrder.rbiOrderId) {
      DatadogLogger.addContext('transaction_id', serverOrder.rbiOrderId);
    }

    decorateLogger({ ...extras, transactionId: serverOrder.rbiOrderId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartEntries, serverOrder.rbiOrderId, serviceMode, store]);

  // Verify cart entries are available
  useEffect(
    () => {
      if (!store.hasSelection || Object.keys(prices).length < 1) {
        return;
      }
      if (!posVendor) {
        return;
      }
      setCartEntries(prevCartEntries =>
        prevCartEntries.filter(cartEntry => cartEntryIsAvailable(cartEntry, posVendor, prices))
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prices]
  );

  const repriceCartEntries = useCallback(
    entries =>
      entries.map(entry => {
        const hasPrice = entry.price !== undefined;
        const isItem = entry.type === CartEntryType.item;
        const isCombo = entry.type === CartEntryType.combo;
        if (isItem) {
          // reprices an item and its grandchild itemOptionModifiers in one go.
          return {
            ...entry,
            // prices are not defined on main items in combos, we need to preserve this
            ...(hasPrice && { price: pricingFunction(entry, entry.quantity) }),
            children: (entry.children || []).map(itemOption => ({
              ...itemOption,
              children: (itemOption.children || []).map(modifier => ({
                ...modifier,
                price: priceForItemOptionModifier({ item: entry, itemOption, modifier })
              }))
            }))
          };
        }
        return {
          ...entry,
          ...(isCombo && hasPrice && { price: pricingFunction(entry, entry.quantity) }),
          children: repriceCartEntries(entry.children)
        };
      }),
    [priceForItemOptionModifier, pricingFunction]
  );

  // recursively reprice cartEntries when prices change
  useEffect(() => {
    setCartEntries(prevEntries => repriceCartEntries(prevEntries));
    // adding repriceCartEntries to the deps array crashes the browser
    // because of its dependency on pricingFunction
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices]);

  const selectStore = useCallback(
    async (newStore, callback) => {
      const hasCartItems = !!cartEntries.length;

      await selectNewStore(newStore, hasCartItems, callback);
    },
    [selectNewStore, cartEntries]
  );

  // show error cart price limit modal
  const isCatering = isCateringOrder(serviceMode);
  useAlertOrderLimit({
    calculateCartTotal,
    checkoutPriceLimit,
    checkoutCateringPriceLimit,
    alertOrderLimit,
    isCatering,
    setCartPriceLimitExceeded
  });
  useAlertOrderDeliveryMinimum({
    calculateCartTotal,
    checkoutDeliveryPriceMinimum,
    isDelivery,
    setCartPriceTooLow
  });
  useAlertOrderCateringMinimum({
    calculateCartTotal,
    checkoutCateringPriceMinimum,
    isCatering,
    setCartCateringPriceTooLow
  });

  const handleReorder = useHandleReorder({
    addToCart,
    navigate,
    posVendor,
    prices,
    pricingFunction,
    setPendingReorder,
    setReordering,
    storeHasSelection: store.hasSelection,
    setUnavailableCartEntries
  });

  const isCartTotalGreaterThanDiscountOfferValue = useCallback(() => {
    const total = calculateCartTotal();

    const selectedDiscountOfferType = selectedOffer?.option?.discountType;
    const selectedDiscountOfferValue = selectedOffer?.option?.discountValue || 0;

    return (
      selectedDiscountOfferType === DiscountValueTypes.AMOUNT &&
      selectedDiscountOfferValue > total / 100
    );
  }, [calculateCartTotal, selectedOffer]);
  const offerCartEntry = offers.selectedOfferCartEntry || null;
  const cartPreviewEntries = offerCartEntry ? [offerCartEntry, ...cartEntries] : cartEntries;
  const isCartEmpty = !cartPreviewEntries.length;
  const canUserCheckout = isNative
    ? !isCartEmpty && !cartPriceLimitExceeded
    : !isCartEmpty && !cartPriceLimitExceeded && !isCartTotalGreaterThanDiscountOfferValue();

  return (
    <OrderContext.Provider
      value={{
        // state
        cartEntries,
        cartVersion: CART_VERSION,
        unavailableCartEntries,
        serviceMode,
        serverOrder,
        cartPriceLimitExceeded,
        cartPriceTooLow,
        cartCateringPriceTooLow,
        cartHasRewardEligibleItem,
        // Catering
        cateringPickupDateTime,
        setOrderCateringPickupDateTime,
        // Cart
        checkoutPriceLimit,
        checkoutDeliveryPriceMinimum,
        checkoutCateringPriceLimit,
        checkoutCateringPriceMinimum,
        alertOrderLimit,
        alertOrderDeliveryMinimum,
        alertOrderCateringMinimum,
        removeFromCart: isKiosk ? removeFromCart : confirmRemoveFromCart,
        removeAllFromCart,
        setUnavailableCartEntries,
        addToCart,
        updateCartEntry,
        updateQuantity,
        calculateCartTotal,
        emptyCart,
        tipAmount,
        setTipAmount,
        updateTipAmount,
        tipSelection,
        setTipSelection,
        verifyCartVersion,
        repriceCartEntries,
        shouldShowTipPercentage,
        isCartTotalGreaterThanDiscountOfferValue,
        isCartEmpty,
        cartPreviewEntries,
        canUserCheckout,
        // Order
        selectServiceMode,
        clearServerOrder,
        selectStore,
        clearCartStoreServiceModeTimeout,
        logCartStoreAndTimeout,
        deliveryAddress,
        setDeliveryAddress,
        deliveryInstructions,
        setDeliveryInstructions,
        deliveryPhoneNumber,
        setDeliveryPhoneNumber,
        kioskUserName,
        resetKioskState,
        isCatering,
        isDelivery,
        setKioskUserName: setKioskUserNameFiltered,
        fireOrderIn,
        setFireOrderIn,
        logOrderLatencyDuration,
        onCommitSuccess,
        tipPercentThresholdCents,
        curbsidePickupOrderId,
        updateShouldSaveDeliveryAddress,
        setCurbsidePickupOrderId,
        curbsidePickupOrderTimePlaced,
        setCurbsidePickupOrderTimePlaced,
        // Server Interactions
        commit,
        price,
        query: getAndRefreshServerOrder,
        fireOrderInXSeconds,
        reorder: {
          handleReorder,
          reordering,
          pendingReorder,
          setReordering,
          setPendingReorder
        },
        recent: {
          setPendingRecentItem,
          pendingRecentItem
        }
      }}
    >
      {props.children}

      <OrderLimitDialog
        body={orderLimitMessage(checkoutPriceLimit, checkoutCateringPriceLimit, isCatering)}
        heading={formatMessage({ id: 'orderLimitHeading' })}
      />
      <RemoveItemDialog
        body={removeItemMessage(itemToRm)}
        heading={formatMessage({ id: 'removeItem' })}
      />
      <ServiceModeDialog
        body={formatMessage({ id: 'changeStoreMessage' })}
        heading={formatMessage({ id: 'changeServiceMode' })}
      />
      <ErrorDialog />
    </OrderContext.Provider>
  );
}

export default OrderContext.Consumer;
